import { Trash2 } from "lucide-react";
import CaseImage from "@/components/CaseImage";
import { formatDate } from "@/lib/dateUtils";
import api, { photoUrl } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function TreatmentUpdatesSection({ updates, caseId, onUpdated }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const canDelete = (u) => user?.role === "admin" || u.staff_id === user?.id;

  const handleDelete = async (updateId) => {
    try {
      await api.delete(`/cases/${caseId}/updates/${updateId}`);
      toast({ title: "Update deleted" });
      onUpdated();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not delete update", description: err.response?.data?.detail });
    }
  };

  return (
    <div className="mb-8">
      <h2 className="font-display text-xl text-amtm-text mb-3">Treatment Updates</h2>
      {updates.length === 0 ? (
        <p className="text-sm text-amtm-textMuted" data-testid="no-updates-message">No treatment updates yet.</p>
      ) : (
        <div className="space-y-3" data-testid="treatment-updates-list">
          {updates.map((u) => (
            <div key={u.id} className="bg-amtm-surface border border-amtm-border rounded-xl p-4">
              <div className="flex items-center justify-between text-xs text-amtm-textMuted mb-1.5">
                <span>{formatDate(u.date)}</span>
                <div className="flex items-center gap-2">
                  <span>{u.staff_name}</span>
                  {canDelete(u) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-treatment-update-button-${u.id}`}
                          aria-label="Delete this update"
                          className="p-1 rounded-full text-amtm-textMuted hover:text-amtm-error hover:bg-amtm-error/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="delete-treatment-update-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this update?</AlertDialogTitle>
                          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-treatment-update-button">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(u.id)}
                            data-testid="confirm-delete-treatment-update-button"
                            className="bg-amtm-error hover:bg-amtm-error/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
              <p className="text-amtm-text">{u.note}</p>
              {u.photos?.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto">
                  {u.photos.map((p) => (
                    <CaseImage key={photoUrl(p)} src={photoUrl(p)} alt="update" className="h-20 w-20 rounded-lg object-cover shrink-0" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
